import { Container, Label } from '@playcanvas/pcui';
import { Mat4, Vec3 } from 'playcanvas';

import { DataPanel } from './data-panel';
import { Events } from '../events';
import { AboutPopup } from './about-popup';
import { setupBottomToolbarStack } from './bottom-toolbar-stack';
import { CameraModeSwitch } from './camera-mode-switch';
import { CombineLcc2Popup } from './combine-lcc2-popup';
import { DownloadPopup } from './download-popup';
import { ExportPopup } from './export-popup';
import { FixPlyDialog } from './fix-ply-dialog';
import { ImageSettingsDialog } from './image-settings-dialog';
import { LeftToolbar } from './left-toolbar';
import { i18n, localize } from './localization';
import { Menu } from './menu';
import { ModeSwitch } from './mode-switch';
import { ModeToggle } from './mode-toggle';
import { OverlayToggle } from './overlay-toggle';
// import logo from './playcanvas-logo.png';
import { Popup, ShowOptions } from './popup';
import { Progress } from './progress';
import { ScenePanel } from './scene-panel';
import { ShortcutsPopup } from './shortcuts-popup';
import { Spinner } from './spinner';
import { StatusBar } from './status-bar';
import { TimelinePanel } from './timeline-panel';
import { Tooltips } from './tooltips';
import { VideoSettingsDialog } from './video-settings-dialog';
import { ViewCube } from './view-cube';
import { ViewModeToggle } from './view-mode-toggle';
import { version } from '../../package.json';
import { VersionCheck, UpdateState } from '../version-check';

// ts compiler and vscode find this type, but eslint does not
type FilePickerAcceptType = unknown;

class EditorUI {
    appContainer: Container;
    topContainer: Container;
    canvasContainer: Container;
    toolsContainer: Container;
    canvas: HTMLCanvasElement;
    popup: Popup;

    constructor(events: Events) {
        // favicon
        // const link = document.createElement('link');
        // link.rel = 'icon';
        // link.href = logo;
        // document.head.appendChild(link);

        // app
        const appContainer = new Container({
            id: 'app-container'
        });

        // editor
        const editorContainer = new Container({
            id: 'editor-container'
        });

        // tooltips container
        const tooltipsContainer = new Container({
            id: 'tooltips-container'
        });

        // top container
        const topContainer = new Container({
            id: 'top-container'
        });

        // canvas
        const canvas = document.createElement('canvas');
        canvas.id = 'canvas';

        // app label — shows version check status then version number
        const appLabel = new Label({
            id: 'app-label',
            text: localize('status-bar.version.checking')
        });
        appLabel.dom.style.cursor = 'pointer';
        let versionTimer: ReturnType<typeof setTimeout> | null = null;

        appLabel.dom.addEventListener('click', (e: MouseEvent) => {
            e.stopPropagation();
            downloadPopup.show();
        });

        // Also listen on pointerdown/pointerup to check if click generation chain is intact
        appLabel.dom.addEventListener('pointerdown', (e: PointerEvent) => {
            console.log('[app-label] pointerdown FIRE target:', (e.target as Element)?.tagName, 'defaultPrevented:', e.defaultPrevented);
            // Stop propagation to prevent PointerController from calling
            // setPointerCapture on canvasContainer, which suppresses click events
            e.stopPropagation();
        });
        appLabel.dom.addEventListener('pointerup', (e: PointerEvent) => {
            console.log('[app-label] pointerup FIRE target:', (e.target as Element)?.tagName, 'defaultPrevented:', e.defaultPrevented);
        });

        // Log all events on document to see if something prevents click generation
        document.addEventListener('pointerdown', (e: PointerEvent) => {
            if ((e.target as Element)?.id === 'app-label') {
                console.log('[document] pointerdown app-label defaultPrevented:', e.defaultPrevented);
            }
        });
        document.addEventListener('pointerup', (e: PointerEvent) => {
            if ((e.target as Element)?.id === 'app-label') {
                console.log('[document] pointerup app-label defaultPrevented:', e.defaultPrevented);
            }
        });
        document.addEventListener('click', (e: MouseEvent) => {
            if ((e.target as Element)?.id === 'app-label') {
                console.log('[document] click FIRE on app-label!');
            }
        });

        // Track the current version status so hovering the label can temporarily
        // reveal the version number and restore the status text on mouse leave.
        let versionStatus: 'checking' | 'available' | 'latest' | 'error' | 'version' = 'checking';
        let statusText = '';

        const showVersionNumber = () => {
            versionStatus = 'version';
            appLabel.text = `RESPLAT v${version}`;
        };

        appLabel.dom.addEventListener('mouseenter', () => {
            if (versionStatus !== 'version') {
                statusText = appLabel.text;
                appLabel.text = `RESPLAT v${version}`;
            }
        });

        appLabel.dom.addEventListener('mouseleave', () => {
            if (versionStatus !== 'version') {
                appLabel.text = statusText;
            }
        });

        events.on('versionCheck.changed', (state: UpdateState) => {
            if (versionTimer) {
                clearTimeout(versionTimer);
                versionTimer = null;
            }
            if (state.status === 'available') {
                versionStatus = 'available';
                appLabel.text = localize('status-bar.version.available');
            } else if (state.status === 'error') {
                versionStatus = 'error';
                appLabel.text = localize('status-bar.version.error');
                versionTimer = setTimeout(showVersionNumber, 1000);
            } else {
                versionStatus = 'latest';
                appLabel.text = localize('status-bar.version.latest');
                versionTimer = setTimeout(showVersionNumber, 1000);
            }
        });

        // operation log label
        const operationLogLabel = new Label({
            id: 'operation-log-label',
            text: ''
        });

        // Track last operation name for undo display
        let lastOperationName = '';
        let lastImportFileName = '';

        const updateOperationLog = (message: string) => {
            operationLogLabel.text = message;
        };

        // cursor label
        const cursorLabel = new Label({
            id: 'cursor-label'
        });

        let fullprecision = '';

        events.on('camera.focalPointPicked', (details: { position: Vec3 }) => {
            cursorLabel.text = `${details.position.x.toFixed(2)}, ${details.position.y.toFixed(2)}, ${details.position.z.toFixed(2)}`;
            fullprecision = `${details.position.x}, ${details.position.y}, ${details.position.z}`;
        });

        ['pointerdown', 'pointerup', 'pointermove', 'wheel', 'dblclick'].forEach((eventName) => {
            cursorLabel.dom.addEventListener(eventName, (event: Event) => event.stopPropagation());
        });

        cursorLabel.dom.addEventListener('pointerdown', () => {
            navigator.clipboard.writeText(fullprecision);

            const orig = cursorLabel.text;
            cursorLabel.text = localize('cursor.copied');
            setTimeout(() => {
                cursorLabel.text = orig;
            }, 1000);
        });

        // canvas container
        const canvasContainer = new Container({
            id: 'canvas-container'
        });

        // tools container
        const toolsContainer = new Container({
            id: 'tools-container'
        });

        // tooltips
        const tooltips = new Tooltips();
        tooltipsContainer.append(tooltips);

        // bottom toolbar
        const scenePanel = new ScenePanel(events, tooltips, canvasContainer);
        const leftToolbar = new LeftToolbar(events, tooltips);
        const modeToggle = new ModeToggle(events, tooltips);
        const viewModeToggle = new ViewModeToggle(events, tooltips);
        const modeSwitch = new ModeSwitch(events, tooltips);
        const overlayToggle = new OverlayToggle(events, tooltips);
        const cameraModeSwitch = new CameraModeSwitch(events, tooltips);
        const menu = new Menu(events, tooltips);

        canvasContainer.dom.appendChild(canvas);
        canvasContainer.append(operationLogLabel);
        canvasContainer.append(cursorLabel);
        canvasContainer.append(toolsContainer);
        canvasContainer.append(scenePanel);
        canvasContainer.append(leftToolbar);
        canvasContainer.append(viewModeToggle);
        canvasContainer.append(modeToggle);
        canvasContainer.append(overlayToggle);
        canvasContainer.append(cameraModeSwitch);
        canvasContainer.append(modeSwitch);
        canvasContainer.append(menu);
        canvasContainer.append(appLabel);

        // view axes container
        const viewCube = new ViewCube(events);
        canvasContainer.append(viewCube);
        events.on('prerender', (cameraMatrix: Mat4) => {
            viewCube.update(cameraMatrix);
        });

        // main container
        const mainContainer = new Container({
            id: 'main-container'
        });

        const timelinePanel = new TimelinePanel(events, tooltips);
        const dataPanel = new DataPanel(events, tooltips);
        const statusBar = new StatusBar(events, tooltips);
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const _versionCheck = new VersionCheck(events, version);

        timelinePanel.hidden = true;

        mainContainer.append(canvasContainer);
        mainContainer.append(timelinePanel);
        mainContainer.append(dataPanel);
        mainContainer.append(statusBar);

        // Wire up status bar panel toggles
        events.on('statusBar.panelChanged', (panel: string | null) => {
            timelinePanel.hidden = panel !== 'timeline';
            dataPanel.hidden = panel !== 'splatData';
        });

        editorContainer.append(mainContainer);

        tooltips.register(cursorLabel, localize('cursor.click-to-copy'), 'bottom');

        // message popup
        const popup = new Popup(tooltips);

        // shortcuts popup
        const shortcutsPopup = new ShortcutsPopup(events);

        // export popup
        const exportPopup = new ExportPopup(events);

        // image settings
        const imageSettingsDialog = new ImageSettingsDialog(events);

        // video settings
        const videoSettingsDialog = new VideoSettingsDialog(events);

        // about popup
        const aboutPopup = new AboutPopup();

        // download dialog (GitHub / Quark mirrors) — opened by the version
        // label and by the web-version "软件版" links
        const downloadPopup = new DownloadPopup();

        // fix ply dialog
        const fixPlyDialog = new FixPlyDialog(events);

        // combine multi-LOD lcc2 dialog
        const combineLcc2Popup = new CombineLcc2Popup(events);

        topContainer.append(popup);
        topContainer.append(exportPopup);
        topContainer.append(fixPlyDialog);
        topContainer.append(combineLcc2Popup);
        topContainer.append(imageSettingsDialog);
        topContainer.append(videoSettingsDialog);
        topContainer.append(shortcutsPopup);
        topContainer.append(aboutPopup);
        topContainer.append(downloadPopup);

        // Remote popups can request the download dialog (e.g. the export
        // popup's "软件版" hint link in the web build).
        events.on('downloadPopup.show', () => downloadPopup.show());

        appContainer.append(editorContainer);
        appContainer.append(topContainer);
        appContainer.append(tooltipsContainer);

        this.appContainer = appContainer;
        this.topContainer = topContainer;
        this.canvasContainer = canvasContainer;
        this.toolsContainer = toolsContainer;
        this.canvas = canvas;
        this.popup = popup;

        document.body.appendChild(appContainer.dom);
        document.body.setAttribute('tabIndex', '-1');
        setupBottomToolbarStack(canvasContainer.dom);

        events.on('show.shortcuts', () => {
            shortcutsPopup.hidden = false;
        });

        events.function('show.exportPopup', (exportType, splatNames: [string], showFilenameEdit: boolean, hasNonLcc: boolean) => {
            return exportPopup.show(exportType, splatNames, showFilenameEdit, hasNonLcc);
        });

        events.function('show.fixPlyDialog', () => {
            return fixPlyDialog.show();
        });

        events.function('show.combineLcc2Popup', async () => {
            const result = await combineLcc2Popup.show();
            if (result) {
                await events.invoke('lcc2.combineLod', result.files, result.name);
            }
        });

        events.function('show.imageSettingsDialog', async () => {
            const imageSettings = await imageSettingsDialog.show();

            if (imageSettings) {
                try {
                    const imageFileTypes: Record<string, {
                        description: string,
                        accept: Record<`${string}/${string}`, `.${string}`[]>,
                        extension: `.${string}`
                    }> = {
                        png: { description: 'PNG Image', accept: { 'image/png': ['.png'] }, extension: '.png' },
                        jpeg: { description: 'JPEG Image', accept: { 'image/jpeg': ['.jpg', '.jpeg'] }, extension: '.jpg' },
                        webp: { description: 'WebP Image', accept: { 'image/webp': ['.webp'] }, extension: '.webp' }
                    };
                    const imageFileType = imageFileTypes[imageSettings.format];
                    let writable: FileSystemWritableFileStream | undefined;
                    let fileHandle: FileSystemFileHandle | undefined;

                    if (window.showSaveFilePicker) {
                        fileHandle = await window.showSaveFilePicker({
                            id: 'ReSplatImageFileExport',
                            types: [{
                                description: imageFileType.description,
                                accept: imageFileType.accept
                            }],
                            suggestedName: `${events.invoke('render.baseFilename')}${imageFileType.extension}`
                        });
                        writable = await fileHandle.createWritable();
                    }

                    const result = await events.invoke('render.image', imageSettings, writable);
                    if (result === false && fileHandle?.remove) {
                        await fileHandle.remove();
                    }
                } catch (error) {
                    if (error instanceof DOMException && error.name === 'AbortError') {
                        return;
                    }
                    await events.invoke('showPopup', {
                        type: 'error',
                        header: i18n.t('panel.render.failed'),
                        message: `'${(error as any).message ?? error}'`
                    });
                }
            }
        });

        events.function('show.videoSettingsDialog', async () => {
            const videoSettings = await videoSettingsDialog.show();

            if (videoSettings) {

                try {
                    // Determine file extension and mime type based on format
                    let fileExtension: string;
                    let filePickerTypes: FilePickerAcceptType[];

                    // Codec name mapping for display
                    const codecNames: Record<string, string> = {
                        'h264': 'H.264',
                        'h265': 'H.265',
                        'vp9': 'VP9',
                        'av1': 'AV1'
                    };
                    const codecName = codecNames[videoSettings.codec] || videoSettings.codec.toUpperCase();

                    if (videoSettings.format === 'webm') {
                        fileExtension = '.webm';
                        filePickerTypes = [{
                            description: `WebM Video (${codecName})`,
                            accept: { 'video/webm': ['.webm'] }
                        }];
                    } else if (videoSettings.format === 'mov') {
                        fileExtension = '.mov';
                        filePickerTypes = [{
                            description: `MOV Video (${codecName})`,
                            accept: { 'video/quicktime': ['.mov'] }
                        }];
                    } else if (videoSettings.format === 'mkv') {
                        fileExtension = '.mkv';
                        filePickerTypes = [{
                            description: `MKV Video (${codecName})`,
                            accept: { 'video/x-matroska': ['.mkv'] }
                        }];
                    } else {
                        fileExtension = '.mp4';
                        filePickerTypes = [{
                            description: `MP4 Video (${codecName})`,
                            accept: { 'video/mp4': ['.mp4'] }
                        }];
                    }

                    const suggested = `${events.invoke('render.baseFilename')}${fileExtension}`;

                    let writable;
                    let fileHandle: FileSystemFileHandle | undefined;

                    if (window.showSaveFilePicker) {
                        fileHandle = await window.showSaveFilePicker({
                            id: 'ReSplatVideoFileExport',
                            types: filePickerTypes,
                            suggestedName: suggested
                        });

                        writable = await fileHandle.createWritable();
                    }

                    const result = await events.invoke('render.video', videoSettings, writable);

                    // if the render was cancelled, remove the empty file left on disk
                    if (result === false && fileHandle?.remove) {
                        await fileHandle.remove();
                    }
                } catch (error) {
                    if (error instanceof DOMException && error.name === 'AbortError') {
                        // user cancelled save dialog
                        return;
                    }

                    await events.invoke('showPopup', {
                        type: 'error',
                        header: 'Failed to render video',
                        message: `'${error.message ?? error}'`
                    });
                }
            }
        });

        events.on('show.about', () => {
            aboutPopup.hidden = false;
        });

        events.function('showPopup', (options: ShowOptions) => {
            // Play error sound for error-type popups. Sound file lives in
            // static/audio/error.mp3 (copied to dist by rollup). Failures are
            // swallowed so a blocked autoplay never blocks the popup itself.
            if (options.type === 'error') {
                try {
                    const url = new URL('static/audio/error.mp3', document.baseURI).toString();
                    const audio = new Audio(url);
                    audio.volume = 0.6;
                    audio.play().catch(() => { /* autoplay policy may block; ignore */ });
                } catch {
                    // audio unavailable — ignore
                }
            }
            return this.popup.show(options);
        });

        // spinner with reference counting to handle nested operations
        const spinner = new Spinner();
        topContainer.append(spinner);

        let spinnerCount = 0;

        events.on('startSpinner', () => {
            spinnerCount++;
            if (spinnerCount === 1) {
                spinner.hidden = false;
            }
        });

        events.on('stopSpinner', () => {
            spinnerCount = Math.max(0, spinnerCount - 1);
            if (spinnerCount === 0) {
                spinner.hidden = true;
                spinner.text = '';  // clear text when spinner hides
            }
        });

        events.on('spinnerText', (text: string) => {
            spinner.text = text;
        });

        // progress

        const progress = new Progress();

        topContainer.append(progress);

        events.on('progressStart', (header: string, cancellable?: boolean) => {
            progress.hidden = false;
            progress.setHeader(header);
            progress.setText('');
            progress.setProgress(0);
            progress.showCancelButton(!!cancellable);
            progress.onCancel = cancellable ? () => events.fire('progressCancel') : null;
        });

        events.on('progressUpdate', (options: { text?: string, progress?: number }) => {
            if (options.text !== undefined) {
                progress.setText(options.text);
            }
            if (options.progress !== undefined) {
                progress.setProgress(options.progress);
            }
        });

        events.on('progressEnd', () => {
            progress.hidden = true;
            progress.showCancelButton(false);
            progress.onCancel = null;
        });

        // initialize canvas to correct size before creating graphics device etc
        const pixelRatio = window.devicePixelRatio;
        canvas.width = Math.ceil(canvasContainer.dom.offsetWidth * pixelRatio);
        canvas.height = Math.ceil(canvasContainer.dom.offsetHeight * pixelRatio);

        ['contextmenu', 'gesturestart', 'gesturechange', 'gestureend'].forEach((event) => {
            document.addEventListener(event, (e) => {
                e.preventDefault();
            }, true);
        });

        // whenever the canvas container is clicked, set keyboard focus on the body
        canvasContainer.dom.addEventListener('pointerdown', (event: PointerEvent) => {
            const target = event.target as Element;
            // set focus on the body if user is busy pressing on the canvas or a child of the tools
            // element
            if (event.target === canvas || toolsContainer.dom.contains(event.target as Node)) {
                document.body.focus();
            }
        }, true);

        // Track if current operation is an import to avoid duplicate logging
        let isImportOperation = false;

        // Helper function to get display names for operations
        const getOperationDisplayName = (opName: string): string => {
            const opDisplayNames: Record<string, string> = {
                'selectAll': '全选',
                'selectNone': '取消选择',
                'selectInvert': '反选',
                'selectOp': '选择',
                'hideSelection': '隐藏选中',
                'unhideAll': '显示全部',
                'deleteSelection': '删除选中',
                'splatsTransform': '变换选中',
                'setPivot': '设置枢轴',
                'paintStroke': '绘画',
                'splatSubdivide': '细分',
                'decalSubdividePaint': '贴花细分绘画',
                'addSplat': '新建包裹球',
                'splatRename': '重命名',
                'addGroup': '新建点云组',
                'deleteGroup': '删除分组',
                'modifyGroupRanges': '修改分组',
                'entityTransform': '实体变换',
                'merge': '合并',
                'stateOp': '状态操作',
                'addShape': '新建包裹球',
                'reset': '重置',
                'animTrackEdit': '动画轨道编辑'
            };
            return opDisplayNames[opName] || opName;
        };

        // Helper function to get display names for transform tools
        const getToolDisplayName = (tool: string): string => {
            const toolDisplayNames: Record<string, string> = {
                'move': '移动',
                'rotate': '旋转',
                'scale': '缩放'
            };
            return toolDisplayNames[tool] || '变换';
        };

        // Listen to operation events
        events.on('edit.add', (editOp: any) => {
            if (editOp.name && editOp.name !== 'addSplat' && editOp.name !== 'addShape') {
                let displayName: string;
                if (editOp.name === 'multiOp') {
                    const tool = events.invoke('tool.active') as string;
                    displayName = getToolDisplayName(tool);
                } else {
                    displayName = getOperationDisplayName(editOp.name);
                }
                lastOperationName = displayName;
                updateOperationLog(displayName);
            } else if (editOp.name === 'addSplat') {
                isImportOperation = true;
                // Try to get filename from splat
                if (editOp.splat && editOp.splat.filename) {
                    lastImportFileName = editOp.splat.filename;
                }
            }
        });

        // Listen to element added events for import operations
        events.on('scene.elementAdded', (element: any) => {
            if (element && element.constructor && element.constructor.name === 'Splat' && isImportOperation) {
                const fileName = element.filename || lastImportFileName || '文件';
                updateOperationLog(`新建：${fileName}`);
                lastOperationName = `新建：${fileName}`;
                isImportOperation = false;
                lastImportFileName = '';
            }
        });

        events.on('edit.undo', () => {
            updateOperationLog(`撤回：${lastOperationName}`);
        });

        events.on('edit.redo', () => {
            updateOperationLog('重做');
        });

        events.on('sphereSelection.create', () => {
            updateOperationLog('新建包裹球');
            lastOperationName = '新建包裹球';
        });

        events.on('boxSelection.create', () => {
            updateOperationLog('新建包裹盒');
            lastOperationName = '新建包裹盒';
        });

        events.on('blockingPlane.create', () => {
            updateOperationLog('新建阻挡平面');
            lastOperationName = '新建阻挡平面';
        });

        events.on('select.all', () => {
            updateOperationLog('全选');
            lastOperationName = '全选';
        });

        events.on('select.none', () => {
            updateOperationLog('取消选择');
            lastOperationName = '取消选择';
        });

        events.on('select.invert', () => {
            updateOperationLog('反选');
            lastOperationName = '反选';
        });

        events.on('select.delete', () => {
            updateOperationLog('删除选中');
            lastOperationName = '删除选中';
        });

        events.on('select.duplicate', () => {
            updateOperationLog('复制选中');
            lastOperationName = '复制选中';
        });

        events.on('select.separate', () => {
            updateOperationLog('分离选中');
            lastOperationName = '分离选中';
        });

        events.on('select.merge', () => {
            updateOperationLog('合并选中');
            lastOperationName = '合并选中';
        });

        events.on('select.hide', () => {
            updateOperationLog('隐藏选中');
            lastOperationName = '隐藏选中';
        });

        events.on('select.unhide', () => {
            updateOperationLog('显示全部');
            lastOperationName = '显示全部';
        });
    }
}

export { EditorUI };
